import React, { useEffect } from "react";
import { Provider, useSelector, useDispatch } from "react-redux";
import { store, persistor } from "./store";
import { PersistGate } from "redux-persist/integration/react";
import {
  BrowserRouter as Router,
  Switch,
  Redirect,
  useHistory 
} from "react-router-dom";
import "moment/locale/es";
import { Route } from "components";
import { RootState } from "reducers";
import { Socket, SocketEvents, Constants, Globals } from 'utils';
import { AuthService } from 'services';
import * as Jetemit from 'jetemit';
import { GoogleOAuthProvider } from '@react-oauth/google';

import "owl.carousel/dist/assets/owl.carousel.css";
import "owl.carousel/dist/assets/owl.theme.default.css";

// Public
import Home from 'screens/home';

// Auth
import Login from "screens/auth/login/login";
import Recover from "screens/auth/login/recover";
import Reset from "screens/auth/login/reset";
import Profile from "screens/profile";
import Register from "screens/auth/register/register";
import Referral from "screens/referral";
import Privacy from "screens/privacy";

// Admin
import AdminUsers from 'screens/admin/users/users';
import AdminConsultationPlans from 'screens/admin/plans/consultation-plans';
import AdminReports from 'screens/admin/reports/reports';
import AdminReportConsultations from 'screens/admin/reports/consultations/consultations';
import AdminReportSales from 'screens/admin/reports/sales/sales';
import AdminReportAppointments from 'screens/admin/reports/appointments/appointments';
import AdminReportPayment from 'screens/admin/reports/payments/payments';
import AdminReferrals from 'screens/admin/referrals/referrals';

// Client
import ClientHome from 'screens/client/home/home';
import ClientApi from 'screens/client/api/api';
import ClientReportConsultations from 'screens/admin/reports/consultations/consultations';
import ClientReportSales from 'screens/admin/reports/sales/sales';
import ClientReportAppointments from 'screens/admin/reports/appointments/appointments';
import ClientInformation from 'screens/client/information/knowledge';
import ClientAgent from 'screens/client/agent/agent';
import ClientTestAI from 'screens/client/test/test';
import ClientReferrals from 'screens/client/referrals/referrals';

// Scripts
import "jquery/dist/jquery.min.js";
import "bootstrap/dist/js/bootstrap.min.js";

// Styles
import "bootstrap/dist/css/bootstrap.min.css";
import "font-awesome/css/font-awesome.min.css";
import "./scss/main.scss";

const Container = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    const getUser = async () => {
      const user = store.getState().user;
      if (user) {
        const res: any = await AuthService.checkUser({
          withoutLoading: true
        });
        if (res.user) {
          dispatch({
            type: 'SET_USER',
            payload: res.user
          });
        }
        else {
          Globals.showError("Lo sentimos, su usuario ha sido eliminado");
          close();
        }
      }
    }
    getUser();

    const close = async () => {
      await dispatch({
        type: 'SET_USER',
        payload: null
      }); 
      await dispatch({
        type: 'SET_ACCESS_TOKEN',
        payload: null
      }); 
      history.replace('/logout');
    }

    Socket.on(SocketEvents.UPDATE_USER,(data: any) => {
      const user = store.getState().user;
      if (user != null && user?.id == data.user_id) {
        getUser();
      }
    });

    Socket.on(SocketEvents.CHATS.MESSAGE,(data: any) => {
      Jetemit.emit(SocketEvents.CHATS.MESSAGE,data);
    });
  },[]);

  return (
    // @ts-ignore
    <GoogleOAuthProvider clientId={ process.env.REACT_APP_GOOGLE_CLIENT_ID }>
      <Switch>
        {/* Public */}
        <Route exact path="/" component={ Home } />

        {/* Auth */}
        <Route exact noAuth path="/login" component={ Login } />
        <Route exact auth path="/profile" component={ Profile } />
        <Route exact noAuth path="/recover" component={ Recover } />
        <Route exact noAuth path="/recover/:code" component={ Reset } />
        <Route exact noAuth path="/:code" component={ Referral } />
        <Route exact noAuth path="/auth/register" component={ Register } />
        <Route exact noAuth path="/auth/privacy" component={ Privacy } />

        {/* Admin */}
        <Route exact admin path="/admin/users" component={ AdminUsers } />
        <Route exact admin path="/admin/plans/consultations" component={ AdminConsultationPlans } />
        <Route exact admin path="/admin/reports" component={ AdminReports } />
        <Route exact admin path="/admin/reports/consultations" component={ AdminReportConsultations } />
        <Route exact admin path="/admin/reports/sales" component={ AdminReportSales } />
        <Route exact admin path="/admin/reports/payments" component={ AdminReportPayment } />
        <Route exact admin path="/admin/referrals" component={ AdminReferrals } />
        <Route exact admin path="/admin/reports/appointments" component={ AdminReportAppointments } />

        {/* Client */}
        <Route exact client path="/client/home" component={ ClientHome } />
        <Route exact client path="/client/api" component={ ClientApi } />
        <Route exact client path="/client/information" component={ ClientInformation } />
        <Route exact client path="/client/agent" component={ ClientAgent } />
        <Route exact client path="/client/reports/consultations" component={ ClientReportConsultations } />
        <Route exact client path="/client/reports/sales" component={ ClientReportSales } />
        <Route exact client path="/client/reports/test" component={ ClientTestAI } />
        <Route exact client path="/client/reports/referrals" component={ ClientReferrals } />
        <Route exact client path="/client/reports/appointments" component={ AdminReportAppointments } />

        <Redirect to="/" />
      </Switch>
    </GoogleOAuthProvider>
  )
}

const App = () => (
  <Provider store={ store }>
    <PersistGate loading={ null } persistor={ persistor }>
      <Router basename={ process.env.REACT_APP_BASENAME }>
        <Container />
      </Router>
    </PersistGate>
  </Provider>
);

export default App;
